/**
 * This plugin is used to add hooks to the store:
 * 1. on store loaded:
 * it fires when the store is loaded first time.
 * if the store is persisted (in indexedDb or in another storage - session or local storage)
 * it fires when the store is loaded from the storage.
 * 2. on store updated:
 * it fires when the store is updated.
 */

import {signal, WritableSignal} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {Observable} from 'rxjs';
import {Store, StorePlugin} from 'signalstory';

export interface StoreHooksPluginHooks<TState extends object> {
	onStoreLoaded?: (state: TState) => void;
	onStoreUpdated?: (state: TState) => void;
}

export type UseStoreHooksPlugin<TState extends object> = StorePlugin &
	StoreHooksPluginHooks<TState> &
	Partial<{
		persistenceType: {
			idb: boolean;
			local: boolean; // localstorage or sessionstorage
		};
		isLoadedFromStorage$: WritableSignal<boolean>; // sets as false in 'init' and true in 'postprocessCommand'
		isLoadedFromStorageObs$: Observable<boolean>;
	}>;

export function useStoreHooks<TState extends object>(hooks: StoreHooksPluginHooks<TState>): UseStoreHooksPlugin<TState> {
	const isLoadedFromStorage$ = signal(false);
	const plugin: UseStoreHooksPlugin<TState> = {
		name: 'store-hooks',
		precedence: 100,
		persistenceType: {
			idb: false,
			local: false,
		},
		isLoadedFromStorage$: isLoadedFromStorage$,
		isLoadedFromStorageObs$: toObservable(isLoadedFromStorage$),
		onStoreLoaded: undefined,
		onStoreUpdated: undefined,
	};

	const HOOKS_LIST = Object.keys(hooks);

	plugin.init = (store: Store<TState>) => {
		plugin.persistenceType = {
			idb: false,
			local: false,
		};
		store.config.plugins.forEach(storePlugin => {
			if (storePlugin.name === 'idb-persistence') {
				plugin.persistenceType.idb = true;
			} else if (storePlugin.name === 'StorePersistence') {
				plugin.persistenceType.local = true;
			}
		});
		HOOKS_LIST.forEach(hook => {
			if (hooks[hook]) {
				plugin[hook] = hooks[hook];
			}
		});
	};
	plugin.postprocessCommand = (store: Store<TState>) => {
		// need to fire the hooks only once
		if (!plugin.isLoadedFromStorage$()) {
			// fire the loaded hook
			plugin.isLoadedFromStorage$.set(true);
			if (plugin.onStoreLoaded) {
				plugin.onStoreLoaded(store.state());
			}
		}
		// fire the updated hook
		if (plugin.onStoreUpdated) {
			plugin.onStoreUpdated(store.state());
		}
	};

	return plugin;
}
